import { useMutation, UseMutationResult } from "@tanstack/react-query";
import { useSnackBar } from "@truevault/aquilo";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useApiTrigger } from "../../common/hooks/api";
import { useDataRequest } from "../../common/hooks/api";
import { NetworkRequestState } from "../../common/models";
import { selfActions } from "../../common/reducers/selfSlice";
import { Api } from "../../common/service/Api";
import { UserDetailsDto } from "../../common/service/server/dto/UserDto";
import { OrganizationPublicId, UUIDString } from "../../common/service/server/Types";
import { Error as RequestsError, Success as RequestsSuccess } from "../copy/requests";
import { isEditModeBlockedError } from "../edit-mode/editModeState";
import { Routes } from "../root/routes";
import { acceptInvitationAndSwitchOrganization } from "../util/organizationNavigation";

type UseInvitation = {
  user: UserDetailsDto;
  actions: {
    acceptInvitation: () => void;
    setPassword: (v: any) => void;
  };
  requests: {
    userRequest: NetworkRequestState;
    acceptRequest: UseMutationResult<void, unknown, void>;
    setPasswordRequest: NetworkRequestState;
  };
};

export const useInvitation = (
  organizationId: OrganizationPublicId,
  invitationId: UUIDString,
): UseInvitation => {
  const api = useCallback(() => Api.user.getSelfByInvitationId(invitationId), [invitationId]);
  const { result: user, request: userRequest } = useDataRequest({
    queryKey: ["invitation", organizationId, invitationId],
    api,
    messages: {},
    notifyError: false,
  });

  const acceptRequest = useAcceptInvitation(organizationId);

  const [setPassword, setPasswordRequest] = useSetPassword(user, organizationId, invitationId);

  return {
    user,
    actions: {
      acceptInvitation: () => acceptRequest.mutate(),
      setPassword,
    },
    requests: {
      userRequest,
      acceptRequest,
      setPasswordRequest,
    },
  };
};

const useAcceptInvitation = (organizationId: OrganizationPublicId) => {
  const enqueueSnackbar = useSnackBar();
  const mutation = useMutation({
    // Loading and errors cover acceptance and the active-org update together.
    mutationFn: () => acceptInvitationAndSwitchOrganization(organizationId),
    onSuccess: () => {
      enqueueSnackbar(RequestsSuccess.AcceptInvitation, { variant: "success" });
    },
    onError: (error) => {
      if (isEditModeBlockedError(error)) {
        mutation.reset();
        return;
      }
      enqueueSnackbar(RequestsError.AcceptInvitation, { variant: "error" });
    },
  });

  return mutation;
};

const useSetPassword = (
  user: UserDetailsDto | null,
  organizationId: OrganizationPublicId,
  invitationId: UUIDString,
) => {
  const dispatch = useDispatch();

  const setPasswordApi = useCallback(
    (values) =>
      Api.user.activateUser(user?.email, organizationId, (values as any).password, invitationId),
    [organizationId, invitationId, user],
  );
  const onSetPasswordDone = useCallback(
    (user) => {
      if (user && user.requiresMfa) {
        window.location.replace(Routes.auth.mfa);
        return;
      }
      dispatch(selfActions.set(user as UserDetailsDto));
      window.location.replace(Routes.root);
    },
    [dispatch],
  );

  return useApiTrigger(
    setPasswordApi,
    onSetPasswordDone,
    RequestsError.AcceptInvitation,
    RequestsSuccess.AcceptInvitation,
  );
};
